/**
 * Workaround (BLP/nx#2201): `crypto.randomUUID()` exists only in a secure context
 * (HTTPS or localhost). A host served over plain HTTP or with an untrusted
 * certificate — the test environment `http://nx-main.demo.blp.local`, a dev server
 * started with `--host` and opened by its LAN address — has `crypto.getRandomValues()`
 * but no `randomUUID`, so every call site throws "crypto.randomUUID is not a function".
 * The first one a user meets is the Write HTML editor
 * (`packages/write/src/vue/html-document.ts`, the per-editor namespace of its
 * preserved HTML fragments), which crashes the B-DMS activity dialog.
 *
 * The shell imports this module before anything else (`main.ts`), so the fallback is
 * in place before the shell or any hosted app calls it. It builds an RFC 4122
 * version 4 UUID from `getRandomValues`, the same shape the native function returns;
 * a native `randomUUID` is never replaced. Remove it once every test environment is
 * served over HTTPS with a trusted certificate (decision on #2201).
 */

type UUID = `${string}-${string}-${string}-${string}-${string}`;

/** A version 4 UUID built from 16 random bytes that `fill` writes. */
export function randomUUIDFromBytes(fill: (bytes: Uint8Array<ArrayBuffer>) => unknown): UUID {
  const bytes = new Uint8Array(16);
  fill(bytes);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40; // version 4
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // RFC 4122 variant
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Adds `randomUUID` to `target` when it lacks one but has `getRandomValues`.
 * Returns whether the fallback was installed.
 */
export function installRandomUUIDFallback(target: Crypto | undefined = globalThis.crypto): boolean {
  if (
    !target ||
    typeof target.randomUUID === 'function' ||
    typeof target.getRandomValues !== 'function'
  ) {
    return false;
  }
  Object.defineProperty(target, 'randomUUID', {
    configurable: true,
    writable: true,
    value: () => randomUUIDFromBytes((bytes) => target.getRandomValues(bytes)),
  });
  return true;
}

installRandomUUIDFallback();
